import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import type * as React from 'react';
import Dog from '../../assets/canyou-dog.svg';
import WhiteLogo from '../../assets/canyou-white-logo.svg';
import { footerColumns, links, socials } from '../foundation/links';
import { colors, tapTarget } from '../foundation/tokens';

/** Site-wide footer for every page: links to every page on the site. */
export const SiteFooter: React.FC = () => (
  <Box
    as="footer"
    bg={colors.inkBg}
    color={colors.footerText}
    px={{ base: '20px', md: '28px' }}
    pt="56px"
    pb="36px"
  >
    <Flex maxW="1180px" mx="auto" direction="column" gap="36px">
      <Grid
        templateColumns={{
          base: 'repeat(2, minmax(0, 1fr))',
          lg: '1.4fr repeat(4, minmax(0, 1fr))',
        }}
        gap="32px"
      >
        <Flex
          direction="column"
          gap="14px"
          gridColumn={{ base: '1 / -1', lg: 'auto' }}
        >
          <Flex align="center" gap="12px">
            <Box filter="invert(1)" aria-hidden="true">
              <Dog width={40} height={44} />
            </Box>
            <Box role="img" aria-label="Canyou">
              <WhiteLogo width={104} />
            </Box>
          </Flex>
          <Text m={0} fontSize="14px" lineHeight={1.65} maxW="30ch">
            Employee onboarding that keeps every worker cleared to work.
          </Text>
          <Box
            as="a"
            href={links.email}
            fontSize="14px"
            color="white"
            alignSelf="flex-start"
            {...tapTarget(11)}
          >
            hello@canyou.com.au
          </Box>
        </Flex>
        {footerColumns.map((col) => (
          <Flex
            key={col.title}
            as="nav"
            aria-label={col.title}
            direction="column"
            // Phones: the links' own padding does the spacing, so each one
            // is a 44px tap target.
            gap={{ base: '2px', md: '10px' }}
            fontSize="14px"
          >
            <Text m={0} color="white" fontWeight={700}>
              {col.title}
            </Text>
            {col.items.map((item) => (
              <Box
                as="a"
                key={item.label}
                href={item.href}
                color={colors.footerText}
                // Taller tap target on phones.
                py={{ base: '11px', md: '2px' }}
                _hover={{ color: 'white' }}
                _focusVisible={{
                  color: 'white',
                  outline: '3px solid',
                  outlineColor: colors.lime,
                  outlineOffset: '4px',
                  borderRadius: '3px',
                }}
              >
                {item.label}
              </Box>
            ))}
          </Flex>
        ))}
      </Grid>
      <Flex
        borderTop="1px solid"
        borderColor={colors.darkLine}
        pt="20px"
        gap="18px"
        justify="space-between"
        wrap="wrap"
        fontSize="13px"
      >
        <span>
          © Canyou. Made in Australia. Photography via Unsplash: Albert Vinas,
          Glenov Brankovic, Shubham Verma, Tim Mossholder, Vitaly Gariev, Adrian
          Sulyok, Cabri Caldwell, Marcus Reubenstein.
        </span>
        <Flex gap="16px" wrap="wrap">
          {socials.map((s) => (
            <Box
              as="a"
              key={s.label}
              href={s.href}
              color={colors.footerText}
              {...tapTarget(12)}
              _hover={{ color: 'white' }}
            >
              {s.label}
            </Box>
          ))}
        </Flex>
      </Flex>
    </Flex>
  </Box>
);
